// src/modulos/Parametros/SubCategoryPage.js
import React, { useState, useMemo, useEffect, useCallback } from 'react';
import useFirestore from '../../hooks/useFirestore';
import useSubCollection from '../../hooks/useSubCollection';
import usePagePermissions from '../../hooks/usePagePermissions';

// Import global components
import {
    PageHeader,
    SearchBar,
    FormModal,
    ConfirmModal,
    TableSkeleton,
    EmptyState,
    Pagination,
} from '../../components/common';

// Import utilities
import { getTimestamps } from '../../utils/timestamps';

// Page-specific styles
import styles from './Parameters.module.css';

// Definições das Coleções
const SUBCATEGORY_COLLECTION = 'parameters/data/sub-categories';
const CATEGORY_COLLECTION = 'parameters/data/product-categories';
const PRODUCTS_COLLECTION = 'products';
const ITEMS_PER_PAGE = 10;

const SubCategoryPage = () => {
    const can = usePagePermissions('sub_categoria');

    // Estados Locais
    const [editingItem, setEditingItem] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [nameInput, setNameInput] = useState('');
    const [categoryId, setCategoryId] = useState('');
    const [typeId, setTypeId] = useState('');
    const [formLoading, setFormLoading] = useState(false);
    const [formError, setFormError] = useState('');
    const [formSuccess, setFormSuccess] = useState('');
    const [currentPage, setCurrentPage] = useState(1);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('');
    const [deleteConfirm, setDeleteConfirm] = useState({ isOpen: false, id: null, name: '' });
    const [deleteLoading, setDeleteLoading] = useState(false);
    const [dependencyCheckLoading, setDependencyCheckLoading] = useState(false);
    const [dependencyError, setDependencyError] = useState('');

    // Hooks de Dados
    const {
        data: subCategories,
        loading: listLoading,
        error: listError,
        addDocument,
        updateDocument,
        deleteDocument,
        refreshData
    } = useFirestore(SUBCATEGORY_COLLECTION, 'name');

    const { data: categories, loading: categoriesLoading } = useFirestore(CATEGORY_COLLECTION, 'name');
    const { data: productTypes, loading: typesLoading } = useSubCollection('parameters', 'types', 'tipo-produto');

    // ============================================================
    // 1. DEFINIR filteredAndSortedItems ANTES dos callbacks
    // ============================================================
    const filteredAndSortedItems = useMemo(() => {
        if (!subCategories) return [];

        let filtered = [...subCategories];

        if (searchTerm) {
            const lowerCaseSearchTerm = searchTerm.toLowerCase();
            filtered = filtered.filter(item =>
                item.name.toLowerCase().includes(lowerCaseSearchTerm) ||
                (item.categoryName && item.categoryName.toLowerCase().includes(lowerCaseSearchTerm)) ||
                (item.typeName && item.typeName.toLowerCase().includes(lowerCaseSearchTerm))
            );
        }

        if (categoryFilter) {
            filtered = filtered.filter(item => item.categoryId === categoryFilter);
        }

        filtered.sort((a, b) => a.name.localeCompare(b.name));
        return filtered;
    }, [subCategories, searchTerm, categoryFilter]);

    const totalFilteredItems = filteredAndSortedItems.length;
    const totalPagesAfterFilter = Math.ceil(totalFilteredItems / ITEMS_PER_PAGE);

    useEffect(() => {
        if (currentPage > totalPagesAfterFilter && totalPagesAfterFilter > 0) {
            setCurrentPage(totalPagesAfterFilter);
        } else if (totalPagesAfterFilter === 0) {
            setCurrentPage(1);
        }
    }, [totalPagesAfterFilter, currentPage]);

    const paginatedItems = useMemo(() => {
        const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
        return filteredAndSortedItems.slice(startIndex, startIndex + ITEMS_PER_PAGE);
    }, [filteredAndSortedItems, currentPage]);

    // Verificar dependências (produtos usando esta subcategoria)
    const checkDependencies = useCallback(async (subCategoryId, subCategoryName) => {
        setDependencyCheckLoading(true);
        setDependencyError('');
        try {
            const { db } = await import('../../firebaseConfig');
            const { collection, query, where, getDocs } = await import('firebase/firestore');

            const productsRef = collection(db, PRODUCTS_COLLECTION);
            const q = query(productsRef, where('subCategoryId', '==', subCategoryId));
            const querySnapshot = await getDocs(q);
            const productCount = querySnapshot.size;

            if (productCount > 0) {
                setDependencyError(`Não é possível excluir a sub-categoria "${subCategoryName}". Existem ${productCount} produto(s) vinculado(s) a ela. Remova ou reassocie os produtos primeiro.`);
                return false;
            }
            return true;
        } catch (error) {
            console.error('Erro ao verificar dependências:', error);
            setDependencyError('Erro ao verificar dependências. Tente novamente.');
            return false;
        } finally {
            setDependencyCheckLoading(false);
        }
    }, []);

    // Reset do formulário
    const resetForm = useCallback(() => {
        setEditingItem(null);
        setNameInput('');
        setCategoryId('');
        setTypeId('');
        setFormError('');
        setFormSuccess('');
    }, []);

    const handleCloseModal = useCallback(() => {
        setIsModalOpen(false);
        resetForm();
    }, [resetForm]);

    // Funções de Ação
    const handleNewClick = useCallback(() => {
        resetForm();
        setIsModalOpen(true);
    }, [resetForm]);

    const handleEditClick = useCallback((item) => {
        setEditingItem(item);
        setNameInput(item.name);
        setCategoryId(item.categoryId || '');
        setTypeId(item.typeId || '');
        setFormError('');
        setFormSuccess('');
        setIsModalOpen(true);
    }, []);

    const handleDeleteClick = useCallback((id, name) => {
        setDeleteConfirm({ isOpen: true, id, name });
        setDependencyError('');
    }, []);

    // Confirmar Delete
    const handleConfirmDelete = useCallback(async () => {
        const { id, name } = deleteConfirm;

        try {
            const canDelete = await checkDependencies(id, name);

            if (!canDelete) {
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                return;
            }

            setDeleteLoading(true);

            const result = await deleteDocument(id);

            if (result.success) {
                setFormSuccess('Sub-categoria deletada com sucesso!');
                setDeleteConfirm({ isOpen: false, id: null, name: '' });

                const totalAfterDelete = totalFilteredItems - 1;
                if (currentPage > 1 && totalAfterDelete <= (currentPage - 1) * ITEMS_PER_PAGE) {
                    setCurrentPage(prev => prev - 1);
                }

                setTimeout(() => setFormSuccess(''), 3000);
            } else {
                setFormError("Erro ao deletar Sub-Categoria: " + result.error);
                setDeleteConfirm({ isOpen: false, id: null, name: '' });
                setTimeout(() => setFormError(''), 5000);
            }
        } catch (error) {
            setFormError('Erro ao deletar sub-categoria. Tente novamente.');
            setDeleteConfirm({ isOpen: false, id: null, name: '' });
        } finally {
            setDeleteLoading(false);
        }
    }, [deleteConfirm, checkDependencies, deleteDocument, totalFilteredItems, currentPage]);

    // Tipos disponíveis baseados na categoria selecionada
    const filteredTypesByCategory = useMemo(() => {
        if (!categoryId) return productTypes;
        return productTypes;
    }, [categoryId, productTypes]);

    // Submissão do formulário
    const handleFormSubmit = useCallback(async (e) => {
        e.preventDefault();
        setFormError('');
        setFormSuccess('');

        // Validação completa
        if (!nameInput.trim()) {
            setFormError("O nome da sub-categoria é obrigatório.");
            return;
        }

        if (!categoryId) {
            setFormError("Selecione uma categoria.");
            return;
        }

        if (!typeId) {
            setFormError("Selecione um tipo de produto.");
            return;
        }

        // Encontrar nomes para persistência
        const categoryName = categories?.find(c => c.id === categoryId)?.name || '';
        const typeName = productTypes?.find(t => t.id === typeId)?.name || '';

        if (!categoryName || !typeName) {
            setFormError("Erro ao encontrar Categoria ou Tipo selecionado.");
            return;
        }

        const userEmail = localStorage.getItem('userEmail') || 'system';
        const timestamps = getTimestamps(editingItem, userEmail);
        const data = {
            name: nameInput.trim(),
            categoryId: categoryId,
            categoryName: categoryName,
            typeId: typeId,
            typeName: typeName,
            ...timestamps,
        };

        setFormLoading(true);

        try {
            let result;
            if (editingItem) {
                result = await updateDocument(editingItem.id, data);
            } else {
                result = await addDocument(data);
            }

            if (result.success) {
                setFormSuccess(editingItem ? 'Sub-categoria editada com sucesso!' : 'Sub-categoria adicionada com sucesso!');
                setTimeout(() => {
                    handleCloseModal();
                }, 1500);
            } else {
                setFormError(`Falha ao ${editingItem ? 'editar' : 'adicionar'}: ${result.error}`);
            }
        } catch (error) {
            console.error("Erro ao salvar Sub-Categoria:", error);
            setFormError('Erro ao salvar no banco de dados. Tente novamente.');
        } finally {
            setFormLoading(false);
        }
    }, [nameInput, categoryId, typeId, editingItem, categories, productTypes, addDocument, updateDocument, handleCloseModal]);

    // Paginação
    const handlePageChange = useCallback((newPage) => {
        if (newPage > 0 && newPage <= totalPagesAfterFilter) {
            setCurrentPage(newPage);
        }
    }, [totalPagesAfterFilter]);

    const handleSearch = useCallback((term) => {
        setSearchTerm(term);
        setCurrentPage(1);
    }, []);

    const handleCategoryFilterChange = useCallback((e) => {
        setCategoryFilter(e.target.value);
        setCurrentPage(1);
    }, []);

    const clearFilters = useCallback(() => {
        setSearchTerm('');
        setCategoryFilter('');
        setCurrentPage(1);
    }, []);

    // Funções auxiliares
    const getCategoryName = useCallback((categoryId) => {
        if (!categoryId) return '-';
        const category = categories?.find(c => c.id === categoryId);
        return category?.name || 'Carregando...';
    }, [categories]);

    const getTypeName = useCallback((typeId) => {
        if (!typeId) return '-';
        const type = productTypes?.find(t => t.id === typeId);
        return type?.name || 'Carregando...';
    }, [productTypes]);

    const hasActiveFilters = searchTerm !== '' || categoryFilter !== '';

    return (
        <div className={styles.pageContainer}>
            {/* Form Modal */}
            <FormModal
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                onSubmit={handleFormSubmit}
                title={editingItem ? `Editar: ${editingItem.name}` : 'Adicionar Nova Sub-Categoria'}
                subtitle={editingItem ? `Editando: ${editingItem.name}` : undefined}
                isLoading={formLoading}
                error={formError}
                success={formSuccess}
                submitText={editingItem ? '💾 Salvar Edição' : '✨ Adicionar Sub-Categoria'}
            >
                <div className={styles.formRow}>
                    <div className={styles.inputGroup}>
                        <label htmlFor="categoryId">
                            Categoria <span className={styles.required}>*</span>
                        </label>
                        <select
                            id="categoryId"
                            value={categoryId}
                            onChange={(e) => {
                                setCategoryId(e.target.value);
                                setTypeId('');
                            }}
                            className={styles.select}
                            disabled={formLoading || categoriesLoading}
                            required
                        >
                            <option value="">{categoriesLoading ? 'Carregando...' : 'Selecione a Categoria'}</option>
                            {categories?.map(cat => (
                                <option key={cat.id} value={cat.id}>{cat.name}</option>
                            ))}
                        </select>
                    </div>

                    <div className={styles.inputGroup}>
                        <label htmlFor="typeId">
                            Tipo de Produto <span className={styles.required}>*</span>
                        </label>
                        <select
                            id="typeId"
                            value={typeId}
                            onChange={(e) => setTypeId(e.target.value)}
                            className={styles.select}
                            disabled={formLoading || typesLoading || !categoryId}
                            required
                        >
                            <option value="">
                                {!categoryId
                                    ? 'Selecione uma categoria primeiro'
                                    : (typesLoading ? 'Carregando...' : 'Selecione o Tipo')}
                            </option>
                            {filteredTypesByCategory?.map(type => (
                                <option key={type.id} value={type.id}>{type.name}</option>
                            ))}
                        </select>
                        {!categoryId && (
                            <span className={styles.fieldHint}>Selecione uma categoria para ver os tipos disponíveis</span>
                        )}
                    </div>
                </div>

                <div className={styles.inputGroup}>
                    <label htmlFor="name">
                        Nome da Sub-Categoria <span className={styles.required}>*</span>
                    </label>
                    <input
                        id="name"
                        type="text"
                        value={nameInput}
                        onChange={(e) => setNameInput(e.target.value)}
                        className={styles.input}
                        placeholder="Ex: Capuz, Luva, Máscara, Embalagem Plástica"
                        disabled={formLoading}
                        required
                    />
                    <span className={styles.fieldHint}>Nome único e descritivo para identificação da sub-categoria</span>
                </div>
            </FormModal>

            {/* Delete Confirmation Modal */}
            <ConfirmModal
                isOpen={deleteConfirm.isOpen}
                onClose={() => setDeleteConfirm({ isOpen: false, id: null, name: '' })}
                onConfirm={handleConfirmDelete}
                title="⚠️ Confirmar Exclusão"
                message={`Tem certeza que deseja deletar a sub-categoria "${deleteConfirm.name}"?${dependencyCheckLoading ? ' Verificando dependências...' : ''}`}
                isLoading={deleteLoading || dependencyCheckLoading}
                danger={true}
            >
                {dependencyError && (
                    <div className={`${styles.alert} ${styles.error}`} style={{ margin: '16px 0' }}>
                        <span className={styles.alertIcon}>⚠️</span>
                        {dependencyError}
                    </div>
                )}
            </ConfirmModal>

            {/* Page Header */}
            <PageHeader
                title="Sub-Categorias de Produto"
                actionText="Adicionar Sub-Categoria"
                onAction={handleNewClick}
                disabled={formLoading || categoriesLoading || typesLoading}
                showAction={can.create}
            />

            {/* Search and Filter Bar */}
            <div className={styles.searchBar}>
                <div style={{ display: 'flex', gap: '120px', flexWrap: 'wrap' }}>
                    <SearchBar
                        placeholder="Buscar por nome, categoria ou tipo..."
                        onSearch={handleSearch}
                        disabled={listLoading}
                    />

                    <select
                        value={categoryFilter}
                        onChange={handleCategoryFilterChange}
                        className={styles.select}
                        style={{ minWidth: '200px' }}
                        disabled={listLoading || categoriesLoading}
                    >
                        <option value="">Todas as Categorias</option>
                        {categories?.map(cat => (
                            <option key={cat.id} value={cat.id}>{cat.name}</option>
                        ))}
                    </select>

                    {hasActiveFilters && (
                        <button onClick={clearFilters} className={styles.cancelButton}>
                            Limpar Filtros
                        </button>
                    )}
                </div>
            </div>

            {/* Error State */}
            {listError && (
                <div className={styles.errorContainer}>
                    <div className={styles.errorIcon}>⚠️</div>
                    <p className={styles.errorText}>Erro ao carregar dados: {listError}</p>
                    <button onClick={refreshData} className={styles.retryButton}>
                        Tentar Novamente
                    </button>
                </div>
            )}

            {/* Table or Loading State */}
            {!listError && (
                <>
                    {listLoading ? (
                        <TableSkeleton columns={4} rows={ITEMS_PER_PAGE} />
                    ) : paginatedItems.length === 0 ? (
                        <EmptyState
                            icon={hasActiveFilters ? '🔍' : '📋'}
                            title={
                                hasActiveFilters
                                    ? 'Nenhum resultado encontrado'
                                    : 'Nenhuma sub-categoria cadastrada'
                            }
                            message={
                                hasActiveFilters
                                    ? 'Tente ajustar os filtros de busca'
                                    : "Clique em 'Adicionar Sub-Categoria' para começar"
                            }
                            actionText={hasActiveFilters ? 'Limpar Filtros' : undefined}
                            onAction={hasActiveFilters ? clearFilters : undefined}
                        />
                    ) : (
                        <>
                            <div className={styles.tableContainer}>
                                <table className={styles.table}>
                                    <thead>
                                        <tr>
                                            <th>Sub-Categoria</th>
                                            <th>Categoria</th>
                                            <th>Tipo de Produto</th>
                                            <th style={{ width: '150px' }}>Ações</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {paginatedItems.map((item) => (
                                            <tr key={item.id}>
                                                <td className={styles.conditionName}>
                                                    <strong>{item.name}</strong>
                                                </td>
                                                <td>{getCategoryName(item.categoryId)}</td>
                                                <td>{getTypeName(item.typeId)}</td>
                                                <td className={styles.actionsCell}>
                                                    {can.edit && (
                                                        <button
                                                            onClick={() => handleEditClick(item)}
                                                            className={styles.actionButton}
                                                            disabled={formLoading}
                                                        >
                                                            Editar
                                                        </button>
                                                    )}
                                                    {can.delete && (
                                                        <button
                                                            onClick={() => handleDeleteClick(item.id, item.name)}
                                                            className={`${styles.actionButton} ${styles.deleteButton}`}
                                                            disabled={formLoading}
                                                        >
                                                            Deletar
                                                        </button>
                                                    )}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>

                            <Pagination
                                currentPage={currentPage}
                                totalPages={totalPagesAfterFilter}
                                totalItems={totalFilteredItems}
                                onPageChange={handlePageChange}
                                disabled={formLoading}
                                itemsLabel="sub-categorias"
                            />
                        </>
                    )}
                </>
            )}
        </div>
    );
};

export default SubCategoryPage;